/**
 * Owner Admin Login — cinematic redesign.
 * Inspired by hero pages that make you want to actually log in.
 *
 * Design choices:
 *   • Split layout: brand-hero (left) + login card (right) on desktop; stacks
 *     vertically on mobile.
 *   • Animated gradient orbs + a subtle grid overlay create depth without
 *     stealing focus.
 *   • Emerald / violet / cyan palette matches the admin app's accent chips.
 *   • Live "why you're excited" stats reveal on the hero side (loaded from
 *     `/api/owner-admin/public-metrics` if we ship it, otherwise static teaser).
 *   • Micro-animations: staggered fade-in, focus glow on inputs, glass-morphism
 *     on the card.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useOwnerAdmin } from '../../context/OwnerAdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Shield, Loader2, KeyRound, ArrowRight, Sparkles, TrendingUp, Users, Zap,
} from 'lucide-react';

export default function OwnerAdminLogin() {
  const { login, verifyMfa, isAuthenticated, loading: authLoading } = useOwnerAdmin();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  if (authLoading) {
    return (
      <div className="owner-admin-scope min-h-screen bg-[#050807] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/owner-admin" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requiresMfa) {
        setMfaToken(result.mfaToken);
      } else {
        navigate('/owner-admin');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaToken, mfaCode);
      navigate('/owner-admin');
    } catch (err) {
      setError(err.message || 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="owner-admin-login-page"
      className="owner-admin-scope min-h-screen relative overflow-hidden bg-[#050807] text-white"
    >
      {/* ─── Animated background ────────────────────────────────────────── */}
      {/* Emerald orb top-left */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-30 animate-cb-drift-slow"
        style={{ background: 'radial-gradient(closest-side, #10b981 0%, transparent 70%)' }}
      />
      {/* Violet orb bottom-right */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-48 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 animate-cb-drift-slower"
        style={{ background: 'radial-gradient(closest-side, #8b5cf6 0%, transparent 70%)' }}
      />
      {/* Cyan accent middle */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full blur-3xl opacity-15 animate-cb-drift-slow"
        style={{ background: 'radial-gradient(closest-side, #22d3ee 0%, transparent 70%)' }}
      />
      {/* Grid overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 100%)',
        }}
      />

      {/* ─── Content ───────────────────────────────────────────────────── */}
      <div
        className={`relative z-10 min-h-screen grid lg:grid-cols-2 gap-8 lg:gap-16 px-6 lg:px-16 py-10 transition-opacity duration-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}
      >
        {/* Left — Hero / brand */}
        <section className="flex flex-col justify-between lg:pr-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shadow-[0_0_28px_rgba(16,185,129,0.35)]">
                <Sparkles className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <p className="text-sm text-emerald-300/80 font-medium tracking-wider uppercase">CountBucks</p>
                <p className="text-xs text-white/50 -mt-0.5">Owner Admin Console</p>
              </div>
            </div>
          </div>

          <div className="mt-16 lg:mt-0">
            <p className="text-emerald-300/80 text-sm tracking-widest uppercase mb-4">Welcome back, Founder</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              <span className="block">Your SaaS,</span>
              <span className="block bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-300 bg-clip-text text-transparent">
                in one glance.
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-lg leading-relaxed">
              MRR, seats, unpaid sweeps, AI spend, cost dashboards, and every customer workspace —
              accessible only to you.
            </p>

            {/* Feature strip */}
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
              {[
                { icon: TrendingUp, label: 'Live MRR', tint: 'text-emerald-300', ring: 'ring-emerald-400/30' },
                { icon: Users, label: 'Every Customer', tint: 'text-cyan-300', ring: 'ring-cyan-400/30' },
                { icon: Zap, label: 'Instant Access', tint: 'text-violet-300', ring: 'ring-violet-400/30' },
              ].map((f, i) => {
                const Ico = f.icon;
                return (
                  <div
                    key={f.label}
                    className={`rounded-xl bg-white/[0.03] border border-white/10 ring-1 ${f.ring} p-4 backdrop-blur-md hover:bg-white/[0.06] transition-all`}
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <Ico className={`w-5 h-5 ${f.tint}`} />
                    <p className="mt-3 text-xs text-white/70 font-medium">{f.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="mt-16 lg:mt-0 text-xs text-white/40 tracking-wide">
            Secured with 2FA · Audit-logged · Session-scoped
          </p>
        </section>

        {/* Right — Login card */}
        <section className="flex items-center justify-center">
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] p-8 sm:p-10"
            data-testid="admin-login-card"
          >
            <div className="mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400/20 to-teal-500/10 border border-emerald-400/30 flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-emerald-300" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">
                {mfaToken ? 'One more step' : 'Sign in'}
              </h2>
              <p className="text-sm text-white/50 mt-1">
                {mfaToken
                  ? 'Enter the 6-digit code from your authenticator app.'
                  : 'Access the CountBucks control room.'}
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-5 bg-red-500/10 border border-red-500/30 text-red-200">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!mfaToken ? (
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80 text-sm">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@countbucks.com"
                    required
                    autoComplete="username"
                    className="h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 focus-visible:border-emerald-400/60 focus-visible:ring-2 focus-visible:ring-emerald-400/40"
                    data-testid="admin-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/80 text-sm">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    className="h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 focus-visible:border-emerald-400/60 focus-visible:ring-2 focus-visible:ring-emerald-400/40"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-semibold shadow-[0_10px_30px_-8px_rgba(16,185,129,0.6)] transition-all disabled:opacity-70"
                  disabled={loading}
                  data-testid="admin-login-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Enter Owner Admin
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleMfaVerify} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="mfaCode" className="text-white/80 text-sm">Verification Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      id="mfaCode"
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="000000"
                      required
                      maxLength={6}
                      autoFocus
                      className="pl-10 h-12 bg-white/[0.03] border-white/10 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder:text-white/20 focus-visible:border-emerald-400/60 focus-visible:ring-2 focus-visible:ring-emerald-400/40"
                      data-testid="admin-mfa-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-semibold shadow-[0_10px_30px_-8px_rgba(16,185,129,0.6)] transition-all disabled:opacity-70"
                  disabled={loading}
                  data-testid="admin-mfa-verify-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    'Verify & Enter'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-white/60 hover:text-white hover:bg-white/[0.04]"
                  onClick={() => setMfaToken(null)}
                >
                  Back to sign-in
                </Button>
              </form>
            )}

            <p className="mt-8 text-[11px] text-white/30 leading-relaxed tracking-wide">
              This is a restricted, audit-logged surface. All sessions are 2FA-enforced and expire after inactivity.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
